import React, { useState, useMemo } from 'react';
import { Registration, EmailAudience } from '../../api/client';

interface RecipientSelectorProps {
  registrations: Registration[];
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  mode?: EmailAudience;
}

type StatusFilter = 'all' | 'approved' | 'checked_in' | 'invited' | 'opened' | 'confirmed' | 'not_confirmed';

const isOpened = (status: string) => status === 'opened' || status === 'invitation_opened';

const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  registrations,
  selectedIds,
  onSelectionChange,
  mode = 'attendees'
}) => {
  const isInvitees = mode === 'invitees';
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>(isInvitees ? 'all' : 'approved');
  const [filterRelationship, setFilterRelationship] = useState<string>('all');

  // Filter registrations
  const filteredRegistrations = useMemo(() => {
    let filtered = [...registrations];

    // Status filter
    if (filterStatus === 'approved') {
      filtered = filtered.filter(r => r.is_approved && r.status !== 'checked_in');
    } else if (filterStatus === 'checked_in') {
      filtered = filtered.filter(r => r.status === 'checked_in');
    } else if (filterStatus === 'invited') {
      filtered = filtered.filter(r => r.status === 'invited');
    } else if (filterStatus === 'opened') {
      filtered = filtered.filter(r => isOpened(r.status));
    } else if (filterStatus === 'confirmed') {
      filtered = filtered.filter(r => !!r.reconfirmed_at);
    } else if (filterStatus === 'not_confirmed') {
      filtered = filtered.filter(r => !r.reconfirmed_at);
    }

    // Relationship filter
    if (filterRelationship !== 'all') {
      filtered = filtered.filter(r => r.relationship_type === filterRelationship);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.name?.toLowerCase().includes(query) ||
        r.email?.toLowerCase().includes(query) ||
        r.company?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [registrations, filterStatus, filterRelationship, searchQuery]);

  const handleSelectAll = () => {
    const allIds = filteredRegistrations.map(r => r.id);
    onSelectionChange(allIds);
  };

  const handleSelectNone = () => {
    onSelectionChange([]);
  };

  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter(sid => sid !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const uniqueRelationships = useMemo(() => {
    const relationships = new Set(registrations.map(r => r.relationship_type).filter(Boolean));
    return Array.from(relationships);
  }, [registrations]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search by name, email, or company..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          {isInvitees ? (
            <>
              <option value="invited">Not Opened</option>
              <option value="opened">Opened</option>
            </>
          ) : (
            <>
              <option value="approved">Approved</option>
              <option value="checked_in">Checked In</option>
              <option value="confirmed">Confirmed coming</option>
              <option value="not_confirmed">Not yet confirmed</option>
            </>
          )}
        </select>

        <select
          value={filterRelationship}
          onChange={(e) => setFilterRelationship(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {uniqueRelationships.map(rel => (
            <option key={rel} value={rel}>{rel}</option>
          ))}
        </select>
      </div>

      {/* Selection controls */}
      <div className="flex items-center justify-between border-y py-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Select All ({filteredRegistrations.length})
          </button>
          <button
            type="button"
            onClick={handleSelectNone}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Select None
          </button>
        </div>
        <span className="text-sm text-gray-600">
          {selectedIds.length} selected
        </span>
      </div>

      {/* Recipients list */}
      <div className="max-h-[400px] overflow-y-auto border rounded-md">
        {filteredRegistrations.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No recipients match your filters
          </div>
        ) : (
          <div className="divide-y">
            {filteredRegistrations.map(registration => {
              const isSelected = selectedIds.includes(registration.id);
              return (
                <label
                  key={registration.id}
                  className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                    isSelected ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggle(registration.id)}
                    className="h-4 w-4 text-blue-600 rounded mr-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{registration.name}</span>
                      {registration.status === 'checked_in' && (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                          Checked In
                        </span>
                      )}
                      {registration.is_approved && registration.status !== 'checked_in' && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                          Approved
                        </span>
                      )}
                      {registration.reconfirmed_at && (
                        <span className="px-2 py-0.5 text-xs bg-teal-100 text-teal-700 rounded">
                          Confirmed coming
                        </span>
                      )}
                      {registration.status === 'invited' && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                          Invited
                        </span>
                      )}
                      {isOpened(registration.status) && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                          Opened
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {registration.email}
                      {registration.company && ` • ${registration.company}`}
                      {registration.relationship_type && ` • ${registration.relationship_type}`}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientSelector;